/**
 * The upload dialog for the operational documents store (Dokumente 2.0, S6).
 *
 * THREE THINGS CHANGED HERE, AND EACH CLOSES A KNOWN DEFECT.
 *
 * 1. THE CATEGORY IS NO LONGER PRE-ANSWERED. It used to open on
 *    "Sonstiges", which is a real category and therefore a real answer —
 *    given silently, on the reader's behalf, every single time. That is how
 *    a miscellaneous folder fills up with documents that belong somewhere
 *    else. The select now starts empty behind a "Kategorie auswählen"
 *    placeholder, and submitting without a choice is refused by name —
 *    "Bitte eine Kategorie auswählen." — exactly as a missing file already
 *    was. "Sonstiges" remains perfectly selectable; it just has to be chosen.
 *
 *    The submit button is NOT greyed out while the form is incomplete. A
 *    dead button states that something is wrong without saying what, and
 *    the reader is left hunting; the toast names the field. This also keeps
 *    the page's existing refusal contract intact rather than replacing it
 *    with a silent one.
 *
 * 2. THE FILE INPUT IS ADDRESSABLE BY ITS LABEL. The visible "Datei" label
 *    was never tied to its input, so assistive technology had a caption
 *    floating beside an unnamed control — and every test in this repo had
 *    to reach for `querySelector('input[type="file"]')` instead of
 *    `getByLabelText("Datei")`. `htmlFor`/`id` fixes both at once. The
 *    pattern is `DepositDocumentsList`'s: a real, focusable, labelled
 *    native input whose button is styled through `file:` utilities — not a
 *    hidden input behind a fake button, which is the other pattern in this
 *    repo and is explicitly `aria-hidden` and untabbable.
 *
 * 3. AN OVERSIZED PICK NO LONGER LEAVES A LIE ON SCREEN. Choosing a valid
 *    file and then an oversized one used to clear the native input while
 *    leaving the first file in state: the dialog went on showing
 *    "Vertrag.pdf · 1.5 KB" above an empty picker, and submitting would
 *    have uploaded a file the reader thought they had replaced.
 *
 * WHAT DID NOT CHANGE. One upload request, then the page's own list and
 * counts refetch — the S4 budget, untouched. Title autofill from the
 * filename, the optional fields, the accepted types, the 25 MB ceiling and
 * the toast conventions are all exactly as they were.
 */
import React, { useId, useRef, useState } from "react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";
import { uploadAdminOperationalDocument } from "../../../api/adminData";
import { formatBytes } from "./documentDisplay";

/** Mirrors the service's own ceiling so the reader hears about it first. */
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

/** What the server accepts, spelled out for the OS file picker. */
const ACCEPTED_TYPES =
  "application/pdf,image/jpeg,image/png,image/webp,image/heic,.heic,.doc,.docx,.xls,.xlsx," +
  "application/msword," +
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.ms-excel," +
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const FIELD =
  "block w-full rounded-[8px] border border-input bg-background px-3 py-2 text-[13px] text-foreground outline-none focus:border-ring";
const LABEL = "mb-1 block text-[11px] font-medium text-muted-foreground";

export default function DocumentUploadDialog({
  open,
  onOpenChange,
  onUploaded,
  categories,
}) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  // "" is "not answered yet" — see the module docstring. It is not a
  // category and the server would reject it, which is the point: the
  // submit refuses while it is the value.
  const [category, setCategory] = useState("");
  const [relatedTo, setRelatedTo] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // A ref ALONGSIDE the state, not instead of it. `submitting` drives what
  // the reader sees and must therefore be state; but a second submit fired
  // before React re-renders reads the same stale `false` from its render
  // closure, so state cannot guard against itself. The ref is written
  // synchronously and is the one both submits actually see.
  const submittingRef = useRef(false);

  // `useId` rather than a module constant: two dialogs on one page would
  // otherwise share an id, and a label would point at the wrong input.
  const fileInputId = `${useId()}-datei`;

  function reset() {
    setFile(null);
    setTitle("");
    setCategory("");
    setRelatedTo("");
    setNotes("");
    submittingRef.current = false;
    setSubmitting(false);
  }

  function handleOpenChange(next) {
    // A request to close is REFUSED while the upload is in flight. Radix
    // routes Escape, the overlay click and the X through here, and before
    // S6 all three closed the dialog under a running request — the reader
    // then had no idea whether their document had arrived. The Cancel
    // button is disabled for the same reason; this covers the three ways
    // round it.
    if (!next && submitting) return;
    if (!next) reset();
    onOpenChange(next);
  }

  function handleFileChange(e) {
    const picked = e.target.files?.[0] || null;
    if (!picked) {
      setFile(null);
      return;
    }
    if (picked.size > MAX_FILE_SIZE_BYTES) {
      toast.error(
        `Datei zu gross (max ${MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB).`
      );
      e.target.value = "";
      // The state goes with the input. Leaving the previous file here is
      // what made the dialog contradict its own picker.
      setFile(null);
      return;
    }
    setFile(picked);
    if (!title) setTitle(picked.name.replace(/\.[^.]+$/, ""));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    // Re-entry guard, and the two halves do different jobs.
    // `disabled={submitting}` below stops the second CLICK, and with it the
    // Enter key, because implicit form submission ignores a disabled submit
    // button. What it cannot stop is a submit event that never consults the
    // button: a programmatic `form.requestSubmit()`, or two native submits
    // landing in the same tick before React has re-rendered `disabled` at
    // all. Reading the ref rather than `submitting` is what makes this line
    // hold in that case — the state both closures would read is still the
    // stale `false` that let them in.
    if (submittingRef.current) return;
    if (!file) {
      toast.error("Bitte eine Datei auswählen.");
      return;
    }
    if (!title.trim()) {
      toast.error("Titel ist erforderlich.");
      return;
    }
    if (!category) {
      toast.error("Bitte eine Kategorie auswählen.");
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await uploadAdminOperationalDocument({
        file,
        title: title.trim(),
        category,
        related_to_label: relatedTo.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      toast.success("Dokument hochgeladen.");
      reset();
      onOpenChange(false);
      onUploaded?.();
    } catch (err) {
      toast.error(err?.message || "Hochladen fehlgeschlagen.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* `closeDisabled` closes the last gap in the S6 busy contract. Escape,
          the overlay and Abbrechen were already refused while a request was
          in flight, but the X went on looking clickable — a control that
          does nothing reads as a broken dialog, not a busy one. `aria-busy`
          follows the repo's form (`loading || undefined`, e.g.
          FinanceAgingCard.jsx:44) so the attribute is absent rather than
          "false" when idle. */}
      <DialogContent closeDisabled={submitting} aria-busy={submitting || undefined}>
        <DialogHeader>
          <DialogTitle>Dokument hochladen</DialogTitle>
          <DialogDescription>
            PDF / Bild / Office-Datei, max. 25 MB.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid gap-3">
          <div>
            <label className={LABEL} htmlFor={fileInputId}>
              Datei
            </label>
            {/* `aria-required`, NOT the native `required`. The native one
                engages browser constraint validation: the submit event
                never fires, a native bubble appears instead, and the S6
                toasts that name the missing field become unreachable —
                replacing a message the reader can act on with one the page
                does not control. `aria-required` carries the same meaning
                to assistive tech and touches no submit path. The same
                reasoning applies to the two fields below. */}
            <input
              id={fileInputId}
              type="file"
              aria-required="true"
              accept={ACCEPTED_TYPES}
              onChange={handleFileChange}
              disabled={submitting}
              className="block w-full text-[12px] text-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-card file:px-3 file:py-1.5 file:text-[12px] file:font-semibold file:text-primary hover:file:bg-muted disabled:opacity-50"
            />
            {file ? (
              <p className="mt-1 text-[11px] text-muted-foreground">
                {file.name} · {formatBytes(file.size)}
              </p>
            ) : null}
          </div>

          <div>
            <label className={LABEL} htmlFor={`${fileInputId}-titel`}>
              Titel
            </label>
            <input
              id={`${fileInputId}-titel`}
              type="text"
              aria-required="true"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              disabled={submitting}
              className={FIELD}
            />
          </div>

          <div>
            <label className={LABEL} htmlFor={`${fileInputId}-kategorie`}>
              Kategorie
            </label>
            <select
              id={`${fileInputId}-kategorie`}
              aria-required="true"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={submitting}
              className={FIELD}
            >
              {/* Disabled, so it can be READ but not chosen: the empty value
                  is a prompt, never an answer. */}
              <option value="" disabled>
                Kategorie auswählen
              </option>
              {categories.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={LABEL} htmlFor={`${fileInputId}-zugehoerig`}>
              Zugehörig zu (optional)
            </label>
            <input
              id={`${fileInputId}-zugehoerig`}
              type="text"
              value={relatedTo}
              onChange={(e) => setRelatedTo(e.target.value)}
              maxLength={200}
              placeholder="z. B. Sercan Atmaca · Apt 3.4"
              disabled={submitting}
              className={FIELD}
            />
          </div>

          <div>
            <label className={LABEL} htmlFor={`${fileInputId}-notizen`}>
              Notizen (optional)
            </label>
            <textarea
              id={`${fileInputId}-notizen`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              disabled={submitting}
              className={FIELD}
            />
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              disabled={submitting}
              className="rounded-[8px] border border-border bg-transparent px-4 py-2 text-[13px] font-semibold text-muted-foreground hover:bg-muted disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              // Disabled only while a request is in flight. Incompleteness
              // is answered by the handler, which names the missing field;
              // see the module docstring on why it is not greyed out.
              disabled={submitting}
              className="rounded-[8px] bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting ? "Lädt …" : "Hochladen"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
